import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  AuthenticateUserState,
  RemoveAuthenticateUser,
} from '../../core/store/authenticate-user.state';
import {
  of,
  Observable,
  BehaviorSubject,
  filter,
  take,
  Subject,
  takeUntil,
} from 'rxjs';
import { UserService } from '../../core/user.service';
import {
  AUTHRETURN_ROUTE,
  CONNECTION_ERROR_MESSAGE,
  DASHBOARD_ROUTE,
  ERROR,
  EMPTY_STRING,
} from '../../shared/constants';
import { RequestFailedError } from '../../core/utils/request-retry';
import { ProfileStoreService } from '../../core/services/profile-store.service';
import { SessionExpiredService } from '../../shared/service/session-expired/session-expired.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RemoveUserProfileData } from '../../core/store/user-profile.state';
import { resolveTokenUserId } from '../../core/utils/resolve-token-user-id';
import { ReturnUrlStoreService } from '../../core/services/return-url-store.service';
import { CustomToastrService } from '../../shared/ngx-toastr/custom-toastr.service';
import { MfaStatusService } from '../../core/services/mfa-status.service';
import { TokenRenewalCoordinatorService } from '../../core/services/token-renewal-coordinator.service';
import { SessionWarningService } from '../../core/services/session-warning.service';
@Component({
  selector: 'app-auth-return',
  templateUrl: './auth-return.component.html',
  styleUrls: ['./auth-return.component.css'],
})
export class AuthReturnComponent implements OnInit, OnDestroy {
  public isLoading = false;
  private destroy$ = new Subject<void>();
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly mfaStatus = inject(MfaStatusService);
  private readonly tokenRenewalCoordinator = inject(
    TokenRenewalCoordinatorService,
  );
  private readonly sessionWarningService = inject(SessionWarningService);
  private isAuthenticated = false;
  private isAuthenticated$: Observable<boolean> = of(false);
  configuration$ = this.oidcSecurityService.getConfiguration();
  userData$ = this.oidcSecurityService.userData$;
  checkSessionChanged$ = this.oidcSecurityService.checkSessionChanged$;
  authenticateUserPrimaryCompanyId = EMPTY_STRING;
  isLoading$ = new BehaviorSubject<boolean>(true);
  /** The profile request never got an answer: show the message and Retry instead of the spinner. */
  connectionError = false;
  readonly connectionErrorMessage = CONNECTION_ERROR_MESSAGE;
  /** Set in ngOnDestroy: the bootstrap can outlive this page (see completeProfileBootstrap). */
  private destroyed = false;
  /** What Retry needs to repeat the bootstrap without a new login. */
  private bootstrapArgs: {
    corViewUserId: string;
    idToken: string;
    accessToken: string;
    loggedInName: string;
  } | null = null;

  constructor(
    private store: Store,
    private userService: UserService,
    private profile: ProfileStoreService,
    private sessionExpiredService: SessionExpiredService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private returnUrlStore: ReturnUrlStoreService,
    private toastr: CustomToastrService,
  ) {
    this.isAuthenticated$ = this.store.select(
      AuthenticateUserState.getIsAuthenticated,
    );
  }

  async ngOnInit() {
    if (this.activatedRoute.snapshot.queryParamMap.get('error')) {
      this.router.navigate(['/login']);
      return;
    }
    this.isLoading = true;
    this.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((authenticated) => {
        this.isAuthenticated = authenticated;
      });
    console.log('local storage isAuthenticated-->', this.isAuthenticated);
    // call below function onetime only
    if (!this.isAuthenticated) {
      // First, ensure the authentication state is checked and updated.
      this.oidcSecurityService
        .checkAuth()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async ({ isAuthenticated, idToken, accessToken, userData }) => {
            this.userService.authApiCallCompleted$.next(false);

            if (isAuthenticated) {
              this.store.dispatch(new RemoveAuthenticateUser());
              this.store.dispatch(new RemoveUserProfileData());
              localStorage.removeItem('logout-event');

              // Three-tier resolution (user_id claim → ID-token decode → sub prefix-strip) so BOTH
              // Auth0 database users and federated SSO users resolve to their real SYRC user id.
              // A bare prefix-strip is correct only for DB users; for SSO users sub is the IdP's
              // own id, and the profile lookup would silently match nothing. See resolve-token-user-id.ts.
              const corViewUserId = resolveTokenUserId(userData, idToken);
              const loggedInName = userData.given_name;

              // Synchronous: flips isAuthenticated=true with ALL FIVE company fields empty. From
              // this line on, AuthGuard's fast path admits any company-scoped route — which is why
              // the grids carry their own CompanyScopeGateService gate. Nothing awaited here can
              // hold that guard shut, because navigation is not driven from this component.
              this.profile.authenticateUser(userData, idToken, accessToken);

              // SYRC-7399: note whether this login performed MFA (from the ID token's `amr`).
              this.mfaStatus.recordLogin(idToken);

              this.sessionExpiredService.resetDialogFlag();

              // AppComponent deliberately skips checkAuth() on this route, and that is the only
              // place it starts renewal and the expiry warning, so a fresh sign-in would
              // otherwise run with neither until the next full page load. Both start() calls
              // are idempotent.
              this.tokenRenewalCoordinator.start();
              this.sessionWarningService.start();

              this.router.events
                .pipe(
                  filter((event) => event instanceof NavigationEnd),
                  take(1),
                  takeUntil(this.destroy$),
                )
                .subscribe(() => {
                  this.isLoading = false;
                });

              // Load roles + company scope BEFORE navigating to the restored deep link. A deep
              // link into a permission-checked route (e.g. a lazy module carrying
              // requiredPermission) would otherwise hit AuthGuard's fast path before roles exist
              // and bounce the user to /no-access. Only after bootstrap do we navigate, then clear
              // the stored value — clearing before a successful navigation would lose the deep link
              // if the user were redirected away.
              this.bootstrapArgs = {
                corViewUserId,
                idToken,
                accessToken,
                loggedInName,
              };
              await this.bootstrapAndNavigate();
            } else {
              this.isLoading = false;
              this.router.navigate(['/login']);
            }
          },
          error: (err) => {
            console.error('checkAuth error:', err);
            this.isLoading = false;
          },
        });
    } else {
      // Already authenticated (persisted NGXS flag). User may still have a stored deep link
      // from a guard interaction before the flag was rehydrated. Restore or fall back to
      // dashboard so the user is never left on the blank /auth-return route.
      this.isLoading = false;
      await this.restoreRequestedRoute();
    }

    //subscribing to isAuthenticated$ to listen for authentication status changes when token is refresh
    this.oidcSecurityService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isAuthenticatedResult) => {
          console.log('isAuthenticatedResult--->', isAuthenticatedResult);

          if (isAuthenticatedResult.isAuthenticated) {
            try {
              this.profile.handleTokenRenewal();
            } catch (err) {
              console.warn(err);
            }
          } else if (
            isAuthenticatedResult.allConfigsAuthenticated.length > 0 &&
            this.isAuthenticated
          ) {
            console.log(
              'Refresh token request failed, redirecting to authorize...',
            );
            // Standard OIDC re-login (prod path). Locally the authority is the CorView emulator,
            // whose /connect/authorize shows the persona picker — no LOCAL branch needed.
            this.tokenRenewalCoordinator.markSignInStarted();
            this.oidcSecurityService.authorize();
          }
        },
        error: (err) => {
          console.error('isAuthenticated$ error:', err);
        },
      });
  }

  /**
   * Retry button on the connection-error state (SYRC-7366). The OIDC session is still valid, so this
   * only repeats the profile bootstrap — the user is never sent back to the login page.
   */
  async retryProfileBootstrap(): Promise<void> {
    if (!this.bootstrapArgs || this.isLoading) {
      return;
    }
    this.connectionError = false;
    this.isLoading = true;
    // The user may have waited on this screen long enough for the tokens to renew. Use the ones in
    // the store, so the bootstrap does not write the login-time (possibly expired) tokens back.
    this.bootstrapArgs = {
      ...this.bootstrapArgs,
      idToken:
        this.store.selectSnapshot(AuthenticateUserState.getIdToken) ||
        this.bootstrapArgs.idToken,
      accessToken:
        this.store.selectSnapshot(AuthenticateUserState.getAccessToken) ||
        this.bootstrapArgs.accessToken,
    };
    await this.bootstrapAndNavigate();
  }

  /** Navigates only once the profile loaded; otherwise the connection-error state is showing. */
  private async bootstrapAndNavigate(): Promise<void> {
    const { corViewUserId, idToken, accessToken, loggedInName } =
      this.bootstrapArgs!;
    const loaded = await this.completeProfileBootstrap(
      corViewUserId,
      idToken,
      accessToken,
      loggedInName,
    );
    if (loaded && !this.userHasMovedOn()) {
      await this.restoreRequestedRoute();
    }
  }

  /**
   * True when the user is already somewhere other than this page or the default landing.
   *
   * The OIDC library navigates to '/' (→ /dashboard) on its own right after the code exchange, so
   * this page can be gone while its bootstrap — retries included — is still running. Restoring the
   * deep link from /dashboard is still wanted (SYRC-6101); pulling the user off a page they opened
   * themselves in the meantime is not.
   */
  private userHasMovedOn(): boolean {
    const path = (this.router.url ?? '').split(/[?#]/)[0];
    return ![
      '',
      '/',
      `/${AUTHRETURN_ROUTE}`,
      `/${DASHBOARD_ROUTE}`,
    ].includes(path);
  }

  /**
   * Profile bootstrap after a successful OIDC callback.
   *
   * These two calls used to be fired side by side and un-awaited, which had two consequences:
   *
   *  1. NON-DETERMINISTIC ORDERING. ensureProfileMenuData() → storeProfileMenuData() reads
   *     `authenticateUserStatePrimaryCompanyId` to decide which primary company's roles/level-3
   *     values to store, and handleUserDetailsAndDispatch() is what puts that company in the store.
   *     Run concurrently, the read frequently won the race and silently fell back to
   *     `_result.primaryCompany[0].id`. Sequencing them makes the company the profile menu is built
   *     for the same one the rest of the app is scoped to.
   *  2. SILENTLY SWALLOWED FAILURES. Neither promise had a rejection handler, so any failure here
   *     surfaced only as an unhandled rejection — the same shape of hole that turned a single 4xx
   *     into a total bootstrap failure in the corviewng port. It now degrades with a warning: the
   *     session stays authenticated and the company-scope gate's timeout releases the grids.
   *
   * Resolves false only when the profile request never got an answer even after retries
   * (RequestFailedError, SYRC-7366). Then nothing usable was loaded, so instead of navigating —
   * which used to end in a logout — the page shows the connection error with a Retry button.
   */
  private async completeProfileBootstrap(
    corViewUserId: string,
    idToken: string,
    accessToken: string,
    loggedInName: string,
  ): Promise<boolean> {
    try {
      await this.profile.handleUserDetailsAndDispatch(
        corViewUserId,
        idToken,
        accessToken,
        loggedInName,
      );
      await this.profile.ensureProfileMenuData(corViewUserId);
      return true;
    } catch (err) {
      if (err instanceof RequestFailedError) {
        if (this.destroyed || this.userHasMovedOn()) {
          // The OIDC library's own navigation (or the user) already left this page, so its Retry
          // state would be set on nothing and the user would sit on the dashboard with no company
          // scope and no explanation. Say why instead, as the old "User not found" toast did.
          console.warn('[AuthReturn] Profile could not be loaded after leaving the page.', err);
          this.toastr.showError(CONNECTION_ERROR_MESSAGE, ERROR);
          return false;
        }
        console.warn('[AuthReturn] Profile could not be loaded; showing Retry.', err);
        this.isLoading = false;
        this.connectionError = true;
        return false;
      }
      console.warn(
        '[AuthReturn] Profile bootstrap failed; continuing with whatever scope resolved. ' +
        'Company-scoped grids will degrade via the CompanyScopeGateService timeout.',
        err,
      );
      return true;
    }
  }

  /**
   * Navigate to the originally requested deep link, falling back to the dashboard when none was
   * stored, then clear the stored value. Always navigates, so the user is never parked on
   * /auth-return. Cleared only AFTER navigateByUrl() resolves/rejects (not before, and not merely
   * after it's issued) — if the navigation is ever cancelled or errors, the deep link is still
   * there to retry rather than gone for good (SYRC-6101 review).
   *
   * Note: the stored value is pathname + search only (see ReturnUrlStoreService / AppComponent);
   * a URL fragment (#anchor) is not preserved. Routes are path-based today, so this is a non-issue
   * — revisit if hash-based deep targets are ever introduced.
   *
   * navigateByUrl can resolve to false when a downstream guard (e.g. RoutePermissionService)
   * rejects the deep link. We still clear (avoids a redirect loop) but log a warning so a
   * silently-failing deep link is visible in production rather than invisible.
   */
  private async restoreRequestedRoute(): Promise<void> {
    const returnUrl = this.returnUrlStore.read();
    const target = returnUrl || `/${DASHBOARD_ROUTE}`;
    try {
      const navigated = await this.router.navigateByUrl(target);
      if (!navigated) {
        console.warn(
          `[AuthReturn] Navigation to restored route "${target}" was rejected ` +
            '(likely a permission guard); the deep link did not resolve.',
        );
      }
    } catch (err) {
      console.warn(
        `[AuthReturn] Navigation to restored route "${target}" errored.`,
        err,
      );
    }
    this.returnUrlStore.clear();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
